export interface EmojiCategory {
  name: string;
  key: string;
  emojis: string[];
  activityTypes: ('animal' | 'adventure' | 'both' | 'all')[];
  description: string;
}

/**
 * Enhanced emoji categories with activity type associations
 * Categories are shown/hidden based on selected activity types for better UX
 */
export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    name: 'Animals',
    key: 'animals',
    emojis: ['🦁', '🐘', '🦒', '🦏', '🐆', '🦓', '🦍', '🐯', '🐼', '🦊', '🐪', '🐨', '🦝', '🦌', '🐺', '🦔'],
    activityTypes: ['animal', 'both'],
    description: 'Wildlife and animal emojis perfect for animal-focused businesses'
  },
  {
    name: 'Marine Life',
    key: 'marine',
    emojis: ['🐋', '🦈', '🐙', '🐠', '🐡', '🦑', '🐢', '🦭', '🐊', '🦞', '🦀', '🐚', '🪸', '🐟', '🐬', '🦜'],
    activityTypes: ['animal', 'both'],
    description: 'Ocean and marine wildlife for aquatic experiences'
  },
  {
    name: 'Adventure',
    key: 'adventure',
    emojis: ['🏔️', '🏕️', '🏖️', '🏞️', '🗺️', '🧭', '🥾', '🎒', '⛰️', '🏃‍♂️', '🧗‍♀️', '🚣‍♂️', '🛶', '🏂', '⛷️', '🪂'],
    activityTypes: ['adventure', 'both'],
    description: 'Outdoor adventure and sports activities'
  },
  {
    name: 'Nature',
    key: 'nature', 
    emojis: ['🌿', '🌳', '🌲', '🌴', '🌵', '🌸', '🌺', '🌻', '🍃', '🌱', '🌾', '🌼', '🍄', '🌰', '🌙', '⭐'],
    activityTypes: ['animal', 'adventure', 'both'],
    description: 'Natural elements and landscapes'
  },
  {
    name: 'Photography',
    key: 'photography',
    emojis: ['📸', '📷', '📹', '🎥', '🔍', '🔭', '🎨', '🖼️', '📱', '💡', '🌅', '🌄', '🌈', '⚡', '🔥', '✨'],
    activityTypes: ['animal', 'adventure', 'both'],
    description: 'Photography and creative elements'
  },
  {
    name: 'Business',
    key: 'business',
    emojis: ['🏢', '🏪', '🏨', '🏰', '🏛️', '🏤', '🏦', '🏫', '🏬', '🏭', '🏗️', '🏘️', '🏙️', '🌆', '🌇', '🌃'],
    activityTypes: ['all'],
    description: 'General business and establishment icons'
  }
];

/**
 * Helper function to get emoji categories based on selected activity types
 */
export const getEmojiCategoriesByActivityTypes = (
  hasAnimalActivities: boolean,
  hasAdventureActivities: boolean
): EmojiCategory[] => {
  // If neither activity type is selected, show only business category
  if (!hasAnimalActivities && !hasAdventureActivities) {
    return EMOJI_CATEGORIES.filter(category => category.activityTypes.includes('all'));
  }

  return EMOJI_CATEGORIES.filter(category => {
    // Always show categories marked as 'all'
    if (category.activityTypes.includes('all')) {
      return true;
    }

    // Show categories that support the selected activity types
    if (hasAnimalActivities && hasAdventureActivities) {
      // Both activity types selected - show categories that support either or both
      return category.activityTypes.includes('animal') ||
             category.activityTypes.includes('adventure') ||
             category.activityTypes.includes('both');
    } else if (hasAnimalActivities) {
      // Only animal activities selected
      return category.activityTypes.includes('animal') ||
             category.activityTypes.includes('both');
    } else if (hasAdventureActivities) {
      // Only adventure activities selected
      return category.activityTypes.includes('adventure') ||
             category.activityTypes.includes('both');
    }

    return false;
  });
};

/**
 * Helper function to get all emojis from visible categories
 */
export const getAllVisibleEmojis = (
  hasAnimalActivities: boolean,
  hasAdventureActivities: boolean
): string[] => {
  const visibleCategories = getEmojiCategoriesByActivityTypes(hasAnimalActivities, hasAdventureActivities);
  return visibleCategories.flatMap(category => category.emojis);
};

/**
 * Helper function to get default emoji based on activity types
 */
export const getDefaultEmoji = (
  hasAnimalActivities: boolean,
  hasAdventureActivities: boolean
): string => {
  if (hasAnimalActivities && hasAdventureActivities) {
    return '🌍'; // Both - use world/nature icon
  } else if (hasAnimalActivities) {
    return '🦁'; // Animal - use lion
  } else if (hasAdventureActivities) {
    return '🏔️'; // Adventure - use mountain
  }
  return '🏢'; // Fallback - use business icon
};

/**
 * Legacy emoji categories object for backward compatibility
 */
export const emojiCategories = {
  animals: EMOJI_CATEGORIES.find(cat => cat.key === 'animals')?.emojis || [],
  nature: EMOJI_CATEGORIES.find(cat => cat.key === 'nature')?.emojis || [],
  adventure: EMOJI_CATEGORIES.find(cat => cat.key === 'adventure')?.emojis || [],
  business: EMOJI_CATEGORIES.find(cat => cat.key === 'business')?.emojis || []
}; 